import styled from "styled-components";
import { Link } from 'react-router-dom';
import { Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import * as TypographyStyles from '../TypographyStyles';
import { purpleColors, violetColors, fontFamily, slateColors } from '../ContentPages/AppLandingPage/styles';

const LIVE_ACTIVITY_CARD_IMAGE =
  '/images/Applandingpage/Live%20Activity/LiveActivity_EN%20small.jpeg';

/** Fixed outer height of the phone bezel (padding included); image scales inside without changing this */
const PHONE_FRAME_HEIGHT = '11rem';

const Container = styled('div')`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 1.5rem;
    gap: 1.25rem;
    box-sizing: border-box;
    /* Same width rule as FeedbackCard; min-width lets nested flex shrink correctly inside column parents */
    width: 100%;
    min-width: 0;
    background: linear-gradient(to bottom right, ${purpleColors[600]}, ${violetColors[600]});
    border-radius: 2.5rem;
    border: 1px solid ${purpleColors[700]};
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    transition: all 0.3s;
    
    &:hover {
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        transform: translateY(-2px);
    }
    
    @media (min-width: 768px) {
        width: min(100%, 400px);
    }
`;

const PhoneAside = styled('div')`
    display: flex;
    justify-content: center;
    flex: 0 0 auto;
    min-width: 0;
    max-width: min(8.75rem, 46%);
    
    @media (min-width: 640px) {
        max-width: min(11rem, 42%);
    }
`;

const PhoneFrame = styled('div')`
    background: ${slateColors[900]};
    border-radius: 1rem;
    padding: 0.3rem;
    box-shadow:
        0 8px 20px -6px rgba(0, 0, 0, 0.35),
        0 0 0 1px rgba(0, 0, 0, 0.12),
        inset 0 0 0 1px rgba(255, 255, 255, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    height: ${PHONE_FRAME_HEIGHT};
    width: 100%;
    max-width: 100%;
`;

const PhoneImage = styled('img')`
    display: block;
    max-height: 100%;
    max-width: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    border-radius: 0.65rem;
`;

const ContentColumn = styled('div')`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
    flex: 1 1 0;
    min-width: 0;
`;

const CardBody = styled('div')`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
`;

const AppButtonLink = styled(Link)`
    display: block;
    text-decoration: none;
    width: 100%;
    box-sizing: border-box;
`;

const AppButton = styled.span`
    font-family: ${fontFamily};
    font-weight: 600;
    font-size: 1rem;
    display: flex;
    padding: 0.75rem 1rem;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    border-radius: 0.75rem;
    border: 2px solid rgba(255, 255, 255, 0.3);
    background: white;
    color: ${purpleColors[600]};
    cursor: pointer;
    transition: all 0.2s;
    width: 100%;
    box-sizing: border-box;
    -webkit-tap-highlight-color: transparent;

    &:hover {
        background: ${slateColors[50]};
        border-color: rgba(255, 255, 255, 0.5);
        color: ${purpleColors[700]};
        transform: translateY(-1px);
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    }

    &:active {
        transform: translateY(0);
        background: ${slateColors[100]};
    }

    &:focus {
        outline: none;
        border-color: rgba(255, 255, 255, 0.6);
        box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.3);
    }

    svg {
        width: 1rem;
        height: 1rem;
        flex-shrink: 0;
    }
`;

const AppLinkCard = () => {
  const { t } = useTranslation();

  return (
    <Container>
      <PhoneAside>
        <PhoneFrame>
          <PhoneImage
            src={LIVE_ACTIVITY_CARD_IMAGE}
            alt={t('journeyDetails.appLinkCard.imageAlt')}
          />
        </PhoneFrame>
      </PhoneAside>
      <ContentColumn>
        <CardBody>
          <TypographyStyles.PageSubtitleSemiBold color="white">
            {t('journeyDetails.appLinkCard.title')}
          </TypographyStyles.PageSubtitleSemiBold>
          <TypographyStyles.BodyLRegular color="rgba(255, 255, 255, 0.95)">
            {t('journeyDetails.appLinkCard.description')}
          </TypographyStyles.BodyLRegular>
        </CardBody>
        <AppButtonLink to="/app">
          <AppButton>
            <Download aria-hidden />
            {t('journeyDetails.appLinkCard.button')}
          </AppButton>
        </AppButtonLink>
      </ContentColumn>
    </Container>
  );
};

export default AppLinkCard;
